import React, { useState } from "react";
import {
  Users,
  UserPlus,
  Mail,
  X,
  Crown,
  Shield,
  User as UserIcon,
  Trash2,
  Copy,
  Check,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { useOrganization } from "../context/OrganizationContext";
import { useAuth } from "../context/AuthContext";

function OrganizationSettings() {
  const { user } = useAuth();
  const {
    organization,
    members,
    inviteUser,
    leaveOrganization,
    fetchOrganization,
  } = useOrganization();

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteSuccess, setInviteSuccess] = useState("");
  const [copied, setCopied] = useState(false);
  const [showLeave, setShowLeave] = useState(false);

  const isOwner = user?.role === "owner" || organization?.owner_id === user?.id;
  const isAdmin = user?.role === "admin" || isOwner;

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;

    setInviting(true);
    setInviteError("");
    setInviteSuccess("");

    const result = await inviteUser(inviteEmail, inviteRole);

    if (result.success) {
      setInviteSuccess(`Invite sent to ${inviteEmail}`);
      setInviteEmail("");
      setCopied(false);
      // Copy invite link to clipboard
      if (result.data.invite_link) {
        navigator.clipboard.writeText(result.data.invite_link);
        setCopied(true);
      }
    } else {
      setInviteError(result.error);
    }
    setInviting(false);
  };

  const handleLeave = async () => {
    if (!window.confirm("Are you sure you want to leave this organization?"))
      return;

    const result = await leaveOrganization();
    if (result.success) {
      window.location.reload();
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      owner: {
        icon: Crown,
        color: "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
      },
      admin: {
        icon: Shield,
        color: "text-blue-400 bg-blue-500/10 border-blue-500/20",
      },
      member: {
        icon: UserIcon,
        color: "text-gray-400 bg-gray-800/50 border-gray-700/50",
      },
      viewer: {
        icon: UserIcon,
        color: "text-gray-500 bg-gray-800/50 border-gray-700/50",
      },
    };
    return badges[role] || badges.member;
  };

  if (!organization) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-gray-800/50 flex items-center justify-center mx-auto mb-4">
          <Users className="w-8 h-8 text-gray-600" />
        </div>
        <p className="text-gray-400">You are not part of any organization</p>
        <p className="text-sm text-gray-500 mt-1">
          Create or join an organization to get started
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Organization Info */}
      <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 border border-gray-800/50 rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-white font-semibold text-lg">
              {organization.name}
            </h3>
            <p className="text-gray-400 text-sm">Slug: {organization.slug}</p>
            <p className="text-gray-500 text-xs mt-1">
              Created: {new Date(organization.created_at).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                isOwner
                  ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                  : isAdmin
                    ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                    : "bg-gray-800/50 text-gray-400 border border-gray-700/50"
              }`}
            >
              {isOwner ? "Owner" : isAdmin ? "Admin" : "Member"}
            </span>
            <span className="text-gray-400 text-sm flex items-center gap-1">
              <Users className="w-4 h-4" />
              {members.length} members
            </span>
          </div>
        </div>
      </div>

      {/* Invite Form */}
      {(isOwner || isAdmin) && (
        <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 border border-gray-800/50 rounded-xl p-6">
          <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-cyan-400" />
            Invite Members
          </h4>

          <form
            onSubmit={handleInvite}
            className="flex flex-col sm:flex-row gap-3"
          >
            <div className="flex-1 relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@example.com"
                required
                className="w-full bg-gray-900/50 border border-gray-800/50 rounded-xl pl-10 pr-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
              />
            </div>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="px-4 py-2.5 bg-gray-900/50 border border-gray-800/50 rounded-xl text-white focus:outline-none focus:border-cyan-500"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <button
              type="submit"
              disabled={inviting}
              className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-200 disabled:opacity-50 whitespace-nowrap"
            >
              {inviting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Invite"
              )}
            </button>
          </form>

          {inviteError && (
            <p className="text-red-400 text-sm mt-2">{inviteError}</p>
          )}
          {inviteSuccess && (
            <div className="mt-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
              <p className="text-green-400 text-sm">{inviteSuccess}</p>
              {copied && (
                <p className="text-gray-400 text-xs mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3 text-green-400" />
                  Invite link copied to clipboard
                </p>
              )}
            </div>
          )}

          <p className="text-xs text-gray-500 mt-3">
            Members will receive an email with instructions to join your
            organization.
          </p>
        </div>
      )}

      {/* Members List */}
      <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 border border-gray-800/50 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-800/50 flex items-center justify-between">
          <h4 className="text-white font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-cyan-400" />
            Members ({members.length})
          </h4>
        </div>

        <div className="divide-y divide-gray-800/50">
          {members.map((member) => {
            const RoleIcon = getRoleBadge(member.role).icon;
            const isCurrentUser = member.id === user?.id;
            return (
              <div
                key={member.id}
                className="px-6 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-600/20 flex items-center justify-center border border-cyan-500/20">
                    <UserIcon className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">
                      {member.full_name || member.email}
                      {isCurrentUser && (
                        <span className="text-gray-500 text-xs ml-2">
                          (you)
                        </span>
                      )}
                    </p>
                    <p className="text-gray-400 text-xs">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getRoleBadge(member.role).color}`}
                  >
                    <RoleIcon className="w-3 h-3" />
                    {member.role}
                  </span>
                  {isOwner && !isCurrentUser && (
                    <button className="p-1 text-gray-500 hover:text-red-400 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Leave Organization */}
      {!isOwner && (
        <div className="bg-gradient-to-br from-red-900/10 to-red-950/10 border border-red-800/20 rounded-xl p-4">
          <p className="text-gray-400 text-sm">
            Leave this organization? You will lose access to all projects and
            data.
          </p>
          <button
            onClick={handleLeave}
            className="mt-2 text-sm text-red-400 hover:text-red-300 transition-colors"
          >
            Leave Organization
          </button>
        </div>
      )}
    </div>
  );
}

export default OrganizationSettings;
